import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { DataTable } from '@/components/DataTable';
import {
  Badge,
  Card,
  ErrorState,
  Loading,
  PageHeader,
  StatCard,
} from '@/components/ui';
import { Link } from 'react-router-dom';
import { formatDate, formatDay, formatMoney, formatNumber, titleCase } from '@/lib/format';
import { BILLING_MODE_LABELS, daysLeftText, useMyLicense } from '@/lib/license';
import { ChannelMeter, LicenseStatusBadge } from '@/components/License';

interface BillingSummary {
  client: {
    name: string;
    currency: string;
    plan_name: string | null;
    plan_code: string | null;
    pricing_model: string | null;
    base_price: string | null;
    included_minutes: number | null;
    billing_interval: string | null;
  } | null;
  subscription: {
    status: string;
    current_period_start: string;
    current_period_end: string;
    provider: string;
  } | null;
  balance: { balance: string; currency: string } | null;
  outstanding: number;
  current_period_usage: {
    minutes: number;
    calls: number;
    charges: number;
    qualified_leads: number;
    transfers: number;
  };
}

interface Invoice {
  id: string;
  number: string;
  status: string;
  kind?: 'LICENSE' | 'USAGE';
  period_start: string;
  period_end: string;
  total: string;
  amount_paid: string;
  currency: string;
  due_at: string | null;
}

const STATUS_TONES: Record<string, 'green' | 'amber' | 'gray' | 'red'> = {
  PAID: 'green',
  OPEN: 'amber',
  DRAFT: 'gray',
  VOID: 'gray',
  UNCOLLECTIBLE: 'red',
};

export default function Billing() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['billing-summary'],
    queryFn: () => api.get<BillingSummary>('/billing/summary'),
  });
  const { data: license } = useMyLicense();
  const showsCosts = license?.shows_usage_costs ?? true;

  return (
    <div className="w-full">
      <PageHeader title="Billing" description="Your plan, usage and invoices." />

      {isPending ? (
        <Loading />
      ) : error ? (
        <ErrorState error={error} retry={() => refetch()} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {showsCosts ? (
              <StatCard
                label="Usage this period"
                value={formatMoney(data.current_period_usage.charges)}
                sub={`${formatNumber(data.current_period_usage.calls)} calls`}
              />
            ) : (
              <StatCard
                label="Monthly licence"
                value={formatMoney(license?.monthly_total, license?.currency)}
                sub={`${formatNumber(data.current_period_usage.calls)} calls this period`}
              />
            )}
            <StatCard
              label="Minutes"
              value={formatNumber(data.current_period_usage.minutes)}
              sub={
                data.client?.included_minutes
                  ? `${formatNumber(data.client.included_minutes)} included`
                  : undefined
              }
            />
            <StatCard
              label="Qualified leads"
              value={formatNumber(data.current_period_usage.qualified_leads)}
              tone="good"
            />
            <StatCard
              label="Outstanding"
              value={formatMoney(data.outstanding)}
              tone={data.outstanding > 0 ? 'warn' : 'default'}
            />
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <Card
              title="Licence"
              actions={
                <Link to="/subscription" className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">
                  Details
                </Link>
              }
            >
              {license?.enforced ? (
                <div className="space-y-4">
                  <dl className="space-y-2 text-sm">
                    <Row label="Status">
                      <LicenseStatusBadge status={license.status} />
                    </Row>
                    <Row label={license.status === 'expired' ? 'Expired' : 'Renews'}>
                      {license.expires_at ? `${formatDay(license.expires_at)} · ${daysLeftText(license)}` : 'No expiry'}
                    </Row>
                    <Row label="Billing">{BILLING_MODE_LABELS[license.billing_mode]}</Row>
                  </dl>
                  <div className="space-y-3">
                    {license.engines.map((engine) => (
                      <ChannelMeter key={engine.kind} engine={engine} compact />
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-ink-500">
                  No channel licence on this account. Usage is billed{' '}
                  {license ? BILLING_MODE_LABELS[license.billing_mode].toLowerCase() : 'per minute'}.
                </p>
              )}
            </Card>

            <Card title="Plan">
              {data.client?.plan_name ? (
                <dl className="space-y-2 text-sm">
                  <Row label="Plan">{data.client.plan_name}</Row>
                  <Row label="Pricing model">
                    {titleCase(data.client.pricing_model ?? '')}
                  </Row>
                  <Row label="Base price">
                    {formatMoney(data.client.base_price, data.client.currency)}
                    {data.client.billing_interval &&
                      ` / ${data.client.billing_interval.toLowerCase()}`}
                  </Row>
                  {data.client.included_minutes ? (
                    <Row label="Included minutes">
                      {formatNumber(data.client.included_minutes)}
                    </Row>
                  ) : null}
                  {data.balance && (
                    <Row label="Account balance">
                      {formatMoney(data.balance.balance, data.balance.currency)}
                    </Row>
                  )}
                </dl>
              ) : (
                <p className="text-sm text-ink-500">
                  {license?.enforced
                    ? 'Your channels are billed as a monthly licence, invoiced before each renewal.'
                    : 'No plan assigned. Usage is billed at the platform default.'}
                </p>
              )}
            </Card>
          </div>
        </>
      )}

      <div className="mt-6">
        <DataTable<Invoice>
          title="Invoices"
          queryKey="invoices"
          path="/billing/invoices"
          searchable={false}
          emptyTitle="No invoices yet"
          emptyDescription="Licence renewals are invoiced a week before they are due; usage, if you are billed for it, at the start of each month."
          columns={[
            {
              key: 'number',
              header: 'Invoice',
              render: (invoice) => (
                <span className="font-mono text-sm">{invoice.number}</span>
              ),
            },
            {
              key: 'kind',
              header: 'Type',
              render: (invoice) => (invoice.kind === 'LICENSE' ? 'Licence' : 'Usage'),
            },
            {
              key: 'period',
              header: 'Period',
              render: (invoice) => (
                <span className="text-xs text-ink-600 dark:text-ink-300">
                  {formatDate(invoice.period_start)} – {formatDate(invoice.period_end)}
                </span>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (invoice) => (
                <Badge tone={STATUS_TONES[invoice.status] ?? 'gray'}>{invoice.status}</Badge>
              ),
            },
            {
              key: 'total',
              header: 'Total',
              numeric: true,
              render: (invoice) => formatMoney(invoice.total, invoice.currency),
            },
            {
              key: 'paid',
              header: 'Paid',
              numeric: true,
              render: (invoice) => formatMoney(invoice.amount_paid, invoice.currency),
            },
            {
              key: 'due',
              header: 'Due',
              render: (invoice) => (
                <span className="text-xs text-ink-500">{formatDate(invoice.due_at)}</span>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-xs uppercase tracking-wide text-ink-500">{label}</dt>
      <dd className="text-right text-ink-800 dark:text-ink-100">{children}</dd>
    </div>
  );
}
